<!-- Insights & Recommendations Card -->
<div class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700">
  <!-- Header -->
  <div class="flex items-center justify-between mb-4">
    <div class="flex items-center gap-2">
      <span class="text-2xl">💡</span>
      <h3 class="text-lg font-bold text-gray-800 dark:text-gray-100">Recommandations</h3>
    </div>
    @if (summary && summary.total > 0) {
      <div class="text-sm text-gray-500 dark:text-gray-400">
        {{ getSummaryText() }}
      </div>
    }
  </div>

  <!-- Loading State -->
  @if (isLoading) {
    <div class="space-y-3 animate-pulse">
      @for (i of [1, 2, 3]; track i) {
        <div
          class="flex items-center gap-4 p-4 bg-gray-50 dark:bg-slate-900 rounded-xl"
          >
          <div class="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-full"></div>
          <div class="flex-1">
            <div class="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4 mb-2"></div>
            <div class="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2"></div>
          </div>
        </div>
      }
    </div>
  }

  <!-- Error State -->
  @if (error && !isLoading) {
    <div class="text-center py-8">
      <span class="text-3xl mb-2 block">⚠️</span>
      <p class="text-gray-500 dark:text-gray-400">{{ error }}</p>
      <button
        (click)="loadInsights()"
        class="mt-3 px-4 py-2 text-sm bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 transition"
        >
        Réessayer
      </button>
    </div>
  }

  <!-- Insights List -->
  @if (!isLoading && !error && insights.length > 0) {
    <div class="space-y-3">
      @for (insight of visibleInsights; track insight; let i = $index) {
        <div
          class="flex items-start gap-4 p-4 rounded-xl border transition-all duration-300 hover:shadow-sm dark:shadow-slate-900/30 cursor-pointer"
          [ngClass]="getPriorityColor(insight.priority)"
          [style.animation-delay]="i * 50 + 'ms'"
          (click)="insight.action ? onActionClick(insight) : null"
          >
          <!-- Icon -->
          <div
            class="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
            [ngClass]="getPriorityIconBg(insight.priority)"
            >
            <span class="text-xl">{{ insight.icon }}</span>
          </div>
          <!-- Content -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <h4 class="font-semibold text-gray-900 dark:text-white truncate">
                {{ insight.title }}
              </h4>
              @if (
                insight.priority === 'critical' || insight.priority === 'high'
                ) {
                <span
                  class="text-xs px-2 py-0.5 rounded-full font-medium"
            [ngClass]="{
              'bg-red-200 text-red-700 dark:text-red-300': insight.priority === 'critical',
              'bg-orange-200 text-orange-700 dark:text-orange-300': insight.priority === 'high',
            }"
                  >
                  {{ getPriorityBadge(insight.priority) }}
                </span>
              }
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
              {{ insight.description }}
            </p>
            <!-- Action Button -->
            @if (insight.action) {
              <button
                (click)="onActionClick(insight); $event.stopPropagation()"
                class="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                {{ insight.action.label }}
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              }
            </div>
          </div>
        }
      </div>
    }

    <!-- Show More/Less -->
    @if (!isLoading && !error && hasMore) {
      <div class="mt-4 text-center">
        <button
          (click)="toggleExpanded()"
          class="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 mx-auto"
          >
          <span>{{
            isExpanded ? "Voir moins" : "Voir tout (" + insights.length + ")"
          }}</span>
          <svg
            class="w-4 h-4 transition-transform"
            [ngClass]="{ 'rotate-180': isExpanded }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      }

      <!-- Empty State -->
      @if (!isLoading && !error && insights.length === 0) {
        <div
          class="text-center py-8"
          >
          <span class="text-4xl mb-2 block">✨</span>
          <p class="text-gray-600 dark:text-gray-400 font-medium">Tout est parfait !</p>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Aucune recommandation pour le moment
          </p>
        </div>
      }
    </div>
